<template>
  <vx-card class="mt-4">
    <div v-if="loading" class="text-center py-8">{{ $t('common.loading') }}</div>

    <div v-else-if="!data.requires_slot_selection" class="text-muted p-2">
      Este plano não usa horários fixos: o aluno agenda as aulas quando quiser,
      dentro da cota.
    </div>

    <div v-else>
      <div class="flex flex-wrap items-center justify-between mb-3">
        <div>
          <div class="font-bold">Grade de horários do aluno</div>
          <small class="text-muted">
            {{ data.quota }} horário(s) por período · {{ data.seats_ahead }} aula(s) já reservada(s) à frente
          </small>
        </div>
        <b-button
          v-if="!editing"
          v-permission="'subscriptions.edit'"
          variant="primary"
          size="sm"
          @click="startEdit">
          <feather-icon icon="EditIcon" class="mr-50"/>
          <span class="align-middle">Trocar horários</span>
        </b-button>
      </div>

      <!-- Grade atual -->
      <div v-if="!editing" class="flex flex-wrap gap-2">
        <vs-chip v-for="slot in data.current" :key="slot.id" color="primary">
          {{ slot.label }}
        </vs-chip>
        <span v-if="!data.current.length" class="text-muted">Sem horários definidos.</span>
      </div>

      <!-- Troca -->
      <div v-else>
        <b-alert show variant="warning" class="p-1 mb-2">
          <div class="flex items-start">
            <feather-icon icon="AlertTriangleIcon" class="mr-1 flex-shrink-0"/>
            <small>
              As aulas já reservadas nos horários antigos serão canceladas e as vagas
              devolvidas ao clube. Os novos horários passam a valer imediatamente.
            </small>
          </div>
        </b-alert>

        <div class="mb-2">
          <span class="font-medium">{{ selected.length }}</span>
          <span class="text-muted"> de {{ data.quota }} horário(s) escolhido(s)</span>
        </div>

        <div v-for="day in data.eligible" :key="day.weekday" class="mb-2">
          <div class="text-muted mb-1"><small class="capitalize">{{ day.label }}</small></div>
          <div class="flex flex-wrap gap-2">
            <b-button
              v-for="slot in day.slots"
              :key="`${slot.lessonId}-${slot.availableScheduleId}`"
              size="sm"
              :variant="isSelected(slot) ? 'primary' : 'outline-primary'"
              :disabled="isFull(slot) && !isSelected(slot)"
              @click="toggle(slot)">
              {{ slot.startTime }} · {{ slot.lessonName }}
              <span class="ml-50">
                <small>{{ availabilityLabel(slot) }}</small>
              </span>
            </b-button>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-3">
          <b-button variant="outline-secondary" size="sm" @click="cancelEdit">
            {{ $t('back') }}
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            :disabled="selected.length !== data.quota"
            @click="save">
            Salvar nova grade
          </b-button>
        </div>
      </div>
    </div>
  </vx-card>
</template>

<script>
import { BAlert, BButton } from 'bootstrap-vue'
import SubscriptionService from '@/services/api/SubscriptionService'

/**
 * Troca dos horários fixos de um assinante.
 *
 * É o pedido de suporte mais comum num estúdio ("mudei de trabalho, só consigo
 * à noite") e até aqui só dava para atender cancelando e revendendo o plano.
 */
export default {
  name: 'SubscriptionSlots',
  components: { BAlert, BButton },
  props: {
    id: { type: [String, Number], required: true },
  },
  data: () => ({
    service: null,
    loading: true,
    editing: false,
    selected: [],
    data: {
      requires_slot_selection: false,
      quota: null,
      current: [],
      eligible: [],
      seats_ahead: 0,
    },
  }),
  mounted() {
    this.service = SubscriptionService.build(this.$vs)
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      this.service.getSlots(this.id).then(
        data => { this.data = data },
        () => this.notifyError(this.$vs, this.$t('messages.error')),
      ).finally(() => { this.loading = false })
    },
    startEdit() {
      // Pré-seleciona a grade atual: trocar um horário de três não deveria
      // obrigar a remontar os outros dois.
      this.selected = this.data.current.map(slot => ({
        lessonId: slot.lesson_id,
        availableScheduleId: slot.available_schedule_id,
        weekday: slot.weekday,
        startTime: slot.start_time,
        endTime: slot.end_time,
      }))
      this.editing = true
    },
    cancelEdit() {
      this.editing = false
      this.selected = []
    },
    slotKey(slot) {
      return `${slot.lessonId}-${slot.weekday}-${slot.startTime}`
    },
    isSelected(slot) {
      return this.selected.some(item => this.slotKey(item) === this.slotKey(slot))
    },
    isFull(slot) {
      return !slot.unlimited && Number(slot.recurringAvailable) <= 0
    },
    toggle(slot) {
      if (this.isSelected(slot)) {
        this.selected = this.selected.filter(item => this.slotKey(item) !== this.slotKey(slot))
        return
      }
      if (this.selected.length >= this.data.quota) return

      this.selected = [...this.selected, {
        lessonId: slot.lessonId,
        availableScheduleId: slot.availableScheduleId,
        weekday: slot.weekday,
        startTime: slot.startTime,
        endTime: slot.endTime,
      }]
    },
    availabilityLabel(slot) {
      if (slot.unlimited) return '(livre)'
      const n = Number(slot.recurringAvailable)
      return n <= 0 ? '(esgotado)' : `(${n} vaga${n === 1 ? '' : 's'})`
    },
    save() {
      this.$vs.loading()
      this.service.updateSlots(this.id, {
        slots: this.selected.map(slot => ({
          lesson_id: slot.lessonId,
          available_schedule_id: slot.availableScheduleId,
          weekday: slot.weekday,
          start_time: slot.startTime,
          end_time: slot.endTime,
        })),
      }).then(
        result => {
          this.notifySuccess(
            this.$vs,
            `Grade atualizada: ${result.created} aula(s) reservada(s), ${result.released} liberada(s).`,
          )
          this.editing = false
          this.load()
        },
        error => this.notifyErrors(this.$vs, error),
      ).finally(() => this.$vs.loading.close())
    },
  },
}
</script>
